import React from 'react';

/**
 * Компонент `FileItem` для отображения файлов и папок в файловом менеджере.
 *
 * @component
 * @example
 * const file = { id: "1", name: "Документы", type: "folder" };
 * const expandedFolders = { "1": true };
 * const selectedFolder = { id: "1", name: "Документы" };
 *
 * return (
 *   <FileItem
 *     item={file}
 *     expandedFolders={expandedFolders}
 *     selectedFolder={selectedFolder}
 *     toggleFolder={toggleFolder}
 *     handleDoubleClick={handleDoubleClick}
 *     level={1}
 *   />
 * );
 *
 * @param {Object} props - Свойства компонента.
 * @param {Object} props.item - Объект файла или папки.
 * @param {string} props.item.id - Уникальный идентификатор файла или папки.
 * @param {string} props.item.name - Название файла или папки.
 * @param {string} props.item.type - Тип элемента ('file' или 'folder').
 * @param {Object} props.expandedFolders - Состояние развернутых папок.
 * @param {Object} props.selectedFolder - Выбранная папка для перемещения.
 * @param {Function} props.toggleFolder - Функция переключения состояния папки.
 * @param {Function} props.handleDoubleClick - Функция выбора папки для перемещения.
 * @param {number} props.level - Уровень вложенности (используется для отступов).
 * @returns {JSX.Element} - Компонент отображения файла или папки.
 */

const ChevronRight = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#888"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const ChevronDown = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#888"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);
const FileItem = ({ item, expandedFolders, selectedFolder, toggleFolder, handleDoubleClick, level, viewMode }) => {
  // Фильтрация элементов по текущему viewMode
  if ((viewMode === 'folders' && item.type !== 'folder') || (viewMode === 'files' && item.type !== 'file')) {
    return null;
  }

  return (
    <div key={item.id} style={{ marginLeft: level * 15 }}>
      {/* Контейнер для элемента (папки или файла) */}
      <div
        className={`file-manager-folder-option ${selectedFolder?.id === item.id ? 'selected' : ''}`}
        onClick={item.type === 'folder' ? (event) => toggleFolder(event, item.id) : undefined} // Только папки реагируют на клик
        onDoubleClick={item.type === 'folder' ? (event) => handleDoubleClick(event, item) : undefined} // Только папки реагируют на двойной клик
        style={{ cursor: item.type === 'folder' ? 'pointer' : 'default' }} // Файлы не интерактивны
      >
        {/* Иконка и имя элемента */}
        <span className="file-manager-folder-label">
          <span className="folder-icon-wrapper">
            {item.type === 'folder' && (expandedFolders[item.id] ? <ChevronDown /> : <ChevronRight />)}
            <img
              src={`/image/${item.type === 'folder' ? 'folder.png' : 'sheet.png'}`}
              alt={item.type === 'folder' ? 'Folder' : 'File'}
              className="file-folder-icon img-size"
            />
          </span>
          <span className="folder-name">{item.name}</span>
        </span>
      </div>
    </div>
  );
};

// Оптимизация ререндеров компонента при неизменных пропсах
export default React.memo(FileItem);
